import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Layers } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDate } from '@/lib/utils';

export function WorkflowRunPage() {
  const { id } = useParams();

  const run = useQuery({
    queryKey: ['run', id],
    queryFn: async () => (await api.get(`/api/workflows/runs/${id}`)).data,
    refetchInterval: (q) => {
      const s = (q.state.data as { status?: string } | undefined)?.status;
      return s === 'QUEUED' || s === 'RUNNING' ? 2000 : false;
    },
  });

  const extractions = useQuery({
    queryKey: ['extractions', 'run', id],
    queryFn: async () =>
      (await api.get(`/api/extractions?workflowRunId=${id}`)).data,
    enabled: !!run.data,
  });

  if (run.isLoading) return <div className="text-sm text-muted-foreground">Loading...</div>;
  if (!run.data) return <div>Not found</div>;

  const r = run.data;

  return (
    <div className="space-y-6">
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
          <Link to="/workflows">
            <ArrowLeft className="mr-2 h-4 w-4" /> Workflows
          </Link>
        </Button>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">{r.workflow.name}</h1>
          <Badge>{r.status}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Run {r.id} · Started {formatDate(r.startedAt)}
        </p>
      </div>

      {r.status === 'AWAITING_REVIEW' && extractions.data?.items?.[0] && (
        <Card className="border-amber-500/50">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <div className="font-medium">Review required</div>
              <div className="text-sm text-muted-foreground">
                A reviewer must approve the AI output before this workflow can complete.
              </div>
            </div>
            <Button asChild>
              <Link to={`/extractions/${extractions.data.items[0].id}`}>
                Open review
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Steps</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(!r.actions || r.actions.length === 0) && (
            <div className="flex flex-col items-center gap-2 rounded-md border border-dashed bg-muted/20 p-6 text-center text-xs text-muted-foreground">
              <Layers className="h-5 w-5 opacity-60" />
              <div>
                No per-step records for this run.
                {r.status === 'AWAITING_REVIEW' && ' The skill output is in the Output card below; reviewer e-sig is the next step.'}
                {r.status === 'COMPLETED' && ' Pre-seeded run with no per-step trace; live runs record one row per step.'}
              </div>
            </div>
          )}
          {r.actions?.map((a: {
            id: string;
            stepName: string;
            stepType: string;
            status: string;
            modelUsed: string | null;
            tokensUsed: number | null;
            cost: number | null;
            startedAt: string | null;
            completedAt: string | null;
            errorMessage: string | null;
          }) => (
            <div key={a.id} className="flex items-center justify-between rounded-md border p-3">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{a.stepName}</span>
                  <Badge variant="outline">{a.stepType}</Badge>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {a.startedAt && <span>Started {formatDate(a.startedAt)}</span>}
                </div>
                {a.errorMessage && (
                  <div className="mt-1 text-xs text-destructive">{a.errorMessage}</div>
                )}
              </div>
              <Badge
                variant={
                  a.status === 'completed'
                    ? 'success'
                    : a.status === 'failed'
                      ? 'destructive'
                      : a.status === 'pending'
                        ? 'warning'
                        : 'info'
                }
              >
                {a.status}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {r.outputData && (
        <Card>
          <CardHeader>
            <CardTitle>Output</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="scrollbar-thin max-h-96 overflow-auto rounded-md bg-muted p-3 text-xs">
              {JSON.stringify(r.outputData, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
